import React, { useState, useCallback, useRef } from 'react';
import { Typography, Box, Button, Chip, Card, CardContent, CardHeader } from '@mui/material';
import { Folder as BrowseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import TagEditor from '../Widgets/TagEditor';

const SettingCreateFolder = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  const [target, setTarget] = useState('C:\\Output\\{year}\\{month}\\Project_{projectid}');
  const [browsingDisabled, setBrowsingDisabled] = useState(false);
  
  const editorRef = useRef(null);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        setTarget(folderPath);
      }
      setBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setBrowsingDisabled(false);
    }
  });

  const actionSettings = useActionSettings({
    settingId,
    caseid,
    actionType: 'createfolder',
    onSaveStatusChange,
    customComparison: (initial) => {
      return target === (initial.target || 'C:\\Output\\{year}\\{month}\\Project_{projectid}');
    },
    customSaveData: () => ({
      target: target
    }),
    loadDataFunction: (data) => {
      if (data.customData?.target !== undefined) {
        setTarget(data.customData.target);
      }
    }
  });

  const handleBrowseClick = () => {
    setBrowsingDisabled(true);
    handleBrowseDirectory();
  };

  return (
    <ActionSettingsLayout
      title={t('auto_create_folder.auto_create_folders')}
      isEnabled={actionSettings.isEnabled}
      onEnabledChange={actionSettings.setIsEnabled}
      isSaveDisabled={actionSettings.isSaveDisabled}
      onSave={actionSettings.handleSave}
      onForceRun={actionSettings.handleForceRun}
      forceRunLoading={actionSettings.forceRunLoading}
      conditions={actionSettings.conditions}
      onConditionsChange={actionSettings.handleConditionsChange}
      popUpOpen={actionSettings.popUpOpen}
      forceRunResult={actionSettings.forceRunResult}
      onCloseStatusWindow={actionSettings.handleCloseStatusWindow}
      forceRunWarningOpen={actionSettings.forceRunWarningOpen}
      forceRunWarningCount={actionSettings.forceRunWarningCount}
      onForceRunConfirm={actionSettings.handleForceRunConfirm}
      onForceRunCancel={actionSettings.handleForceRunCancel}
      tags={tags}
      primaryColor="#5757b0"
    >
      {/* Where to create folders */}
      <Card sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #5757b010',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#5757b0',
                color: 'white',
                borderRadius: '6px',
                px: 1.5,
                py: 0.7,
                mr: { xs: 1, sm: 2 },
                fontSize: '0.85rem',
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
              }}>
                {t('auto_create_folder.where_label')}
              </Box>
              {t('auto_create_folder.where_to_create')}
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{
            mb: 2,
            border: '1px solid #d0d0e8',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(87, 87, 176, 0.05)',
            backgroundColor: '#fff',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#5757b0',
              boxShadow: '0 8px 20px rgba(87, 87, 176, 0.1)',
            },
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'stretch',
            }}>
              <Box sx={{
                flex: 1,
                padding: '12px 16px',
                minHeight: '28px',
                fontFamily: 'SF Mono, Consolas, Monaco, monospace',
                overflow: 'hidden',
                borderBottom: { xs: '1px solid #eaeaf0', sm: 'none' },
              }}>
                <TagEditor
                  ref={editorRef}
                  value={target}
                  onChange={setTarget}
                  placeholder={t('auto_create_folder.folder_path_placeholder')}
                  minHeight="28px"
                  singleLine
                  tags={tags}
                />
              </Box>
              <Button
                variant="contained"
                onClick={handleBrowseClick}
                disabled={browsingDisabled}
                sx={{
                  height: { xs: '40px', sm: 'auto' },
                  width: { xs: '100%', sm: 'auto' },
                  borderRadius: { xs: '0', sm: '0 12px 0 0' },
                  backgroundColor: '#5757b0',
                  color: 'white',
                  m: 0,
                  px: 2.5,
                  '&:hover': {
                    backgroundColor: '#4a4a90',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#c0c0d0',
                    color: 'white',
                  },
                  borderLeft: { sm: '1px solid #5757b060' },
                  transition: 'all 0.2s ease',
                }}
              >
                <BrowseIcon />
              </Button>
            </Box>

            <Box sx={{
              p: 2,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              backgroundColor: '#f8f9ff',
              borderTop: '1px solid #eaeaf0',
            }}>
              <Typography sx={{
                fontWeight: 600,
                color: '#5757b0',
                fontSize: '0.85rem',
                mr: 1,
                display: 'flex',
                alignItems: 'center',
              }}>
                Quick Tags:
              </Typography>
              {[
                { label: 'Year', value: 'year' },
                { label: 'Month', value: 'month' },
                { label: 'Day', value: 'day' },
                { label: 'Project ID', value: 'projectid' },
                { label: 'Filename', value: 'filename' },
              ].map(tag => (
                <Chip
                  key={tag.value}
                  label={tag.label}
                  onClick={() => {
                    if (editorRef.current) {
                      editorRef.current.focus();
                      setTimeout(() => {
                        editorRef.current.insertTag(tag.value);
                      }, 10);
                    }
                  }}
                  size="small"
                  sx={{
                    backgroundColor: '#f0f0ff',
                    color: '#5757b0',
                    borderColor: '#5757b030',
                    border: '1px solid',
                    '&:hover': {
                      backgroundColor: '#e0e0ff',
                      boxShadow: '0 2px 6px rgba(87, 87, 176, 0.15)',
                    },
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                  }}
                />
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {FolderBrowserComponent}
    </ActionSettingsLayout>
  );
};

export default React.memo(SettingCreateFolder);
